import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import throttle from 'lodash/throttle';

import Select from '../select/select.component';
import { OptionsPropType } from '../propTypes/common';
import MultiValue from './value.component';
import Button from '../button.component';

class TruncatableSelect extends PureComponent {
  static propTypes = {
    className: PropTypes.string,
    selectComponentClassName: PropTypes.string,
    onChange: PropTypes.func,
    onToggle: PropTypes.func,
    multi: PropTypes.bool,
    value: OptionsPropType,
  };

  static defaultProps = {
    multi: true,
  };

  constructor(props) {
    super(props);
    this.throttledResizeHandler = throttle(this.measureFirstRow, 100);
    this.selectComponents = { MultiValue: this.renderMultiValue };
  }

  state = {
    collapsed: true,
  };

  componentDidMount() {
    this.observeValueContainer();
    this.measureFirstRow();
    window.addEventListener('resize', this.throttledResizeHandler);
  }

  componentDidUpdate(prevProps) {
    if (!this.resizeObserver) {
      this.observeValueContainer();
    }

    if (this.props.value !== prevProps.value) {
      this.measureFirstRow();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.throttledResizeHandler);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  getValueContainer = () => this.divRef && this.divRef.querySelector('.Select__value-container');

  observeValueContainer = () => {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    const container = this.getValueContainer();
    if (!container) {
      return;
    }
    this.resizeObserver = new ResizeObserver(this.measureFirstRow);
    this.resizeObserver.observe(container);
  };

  measureFirstRow = () => {
    if (!this.state.collapsed) {
      return;
    }
    if (this.divRef && this.divRef.querySelector('.Select__control--menu-is-open')) {
      return;
    }
    const container = this.getValueContainer();
    if (!container) {
      return;
    }

    const values = Array.from(container.querySelectorAll('.Select__multi-value'));
    if (!values.length) {
      this.setNumberOfValuesToDisplay(0);
      return;
    }

    const tops = values.map(node => Math.round(node.getBoundingClientRect().top));
    const firstRowTop = tops.reduce((min, top) => Math.min(min, top), Infinity);
    const numberOfValuesToDisplay = tops.filter(top => top <= firstRowTop + 2).length;

    this.setNumberOfValuesToDisplay(numberOfValuesToDisplay);
  };

  setNumberOfValuesToDisplay = numberOfValuesToDisplay => {
    if (this.state.numberOfValuesToDisplay !== numberOfValuesToDisplay) {
      this.setState({ numberOfValuesToDisplay });
    }
  };

  toggle = () => {
    const { onToggle } = this.props;
    this.setState(prevState => ({
      collapsed: !prevState.collapsed,
    }), () => {
      this.measureFirstRow();
      if (onToggle) {
        onToggle(this.state.collapsed);
      }
    });
  };

  onChange = elements => {
    const { onChange } = this.props;
    if (!elements || !elements.length) {
      this.setState({
        collapsed: true,
      });
    }

    if (onChange) {
      onChange(elements);
    }
  };

  renderMultiValue = props => <MultiValue {...props} />;

  render() {
    const { selectComponentClassName, className, ...props } = this.props;
    const { collapsed, numberOfValuesToDisplay } = this.state;
    const selectedValues = props.value || [];
    const numberHiddenValues = selectedValues.length - numberOfValuesToDisplay;
    return (
      <div ref={ref => this.divRef = ref} className={classnames(className, 'select--truncatable__wrapper')}>
        <Select
          className={classnames(selectComponentClassName, 'sdk-custom-truncatableSelect', {
            'select--collapsed': collapsed,
          })}
          ref={component => this.selectRef = component}
          components={this.selectComponents}
          {...props}
          onChange={this.onChange}
        />
        { !numberHiddenValues || numberHiddenValues < 1 || !selectedValues.length
          ? null
          : (
            <Button
              className="show-more-link"
              onClick={this.toggle}
              kind="transparent"
              tabIndex="0"
            >
              {collapsed ? `show ${numberHiddenValues} more` : `show ${numberHiddenValues} less` }
            </Button>
          )
        }
      </div>
    );
  }
}

export default TruncatableSelect;
